import {
  Box,
  List,
  Chip,
  Avatar,
  ListItem,
  Typography,
  ListItemIcon,
  ListItemText,
  ListItemButton,
} from '@mui/material';

import { useTranslate } from 'src/locales';

import { Image } from 'src/components/image';

type ProviderData = {
  value: string;
  channel_name: string;
  icon: string;
  description: string;
  isPopular?: boolean;
};

type ProviderListProps = {
  providers: ProviderData[];
  selectedProvider: string | null;
  onSelect: (value: string) => void;
  onDoubleClick?: (value: string) => void;
};

export function ProviderList({
  providers,
  selectedProvider,
  onSelect,
  onDoubleClick,
}: ProviderListProps) {
  const { t } = useTranslate('translation');

  return (
    <List sx={{ p: 0 }}>
      {providers.map((data, index) => {
        const isSelected = selectedProvider === data?.value;

        return (
          <ListItem key={index} disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              selected={isSelected}
              onClick={() => onSelect(data?.value)}
              onDoubleClick={() => onDoubleClick?.(data?.value)}
              sx={{
                borderRadius: 2,
                border: 1,
                borderColor: isSelected ? 'primary.main' : 'divider',
                backgroundColor: isSelected ? 'primary.alpha4' : 'transparent',
                py: 2.5,
                px: 3,
                '&.Mui-selected': {
                  backgroundColor: 'primary.alpha4',
                  '&:hover': {
                    backgroundColor: 'primary.alpha8',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 56 }}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: 'grey.100',
                    p: 1,
                  }}
                >
                  <Image
                    src={data.icon}
                    alt={data.icon}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Avatar>
              </ListItemIcon>

              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {data.channel_name}
                    </Typography>
                    {data.isPopular && (
                      <Chip
                        label={t('POPULAR')}
                        size="small"
                        color="secondary"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                    {data.description}
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
}
